type ScheduleCadence = {
  type?: unknown;
  label?: unknown;
  intervalSeconds?: unknown;
  interval_seconds?: unknown;
  seconds?: unknown;
  intervalValue?: unknown;
  interval_value?: unknown;
  intervalUnit?: unknown;
  interval_unit?: unknown;
  times?: unknown;
  timezone?: unknown;
  daysOfWeek?: unknown;
  daysOfMonth?: unknown;
  ordinalWeekday?: {
    ordinal?: unknown;
    weekday?: unknown;
  } | null;
  market?: unknown;
  event?: unknown;
  offsetMinutes?: unknown;
};

type ScheduleLike = ScheduleCadence & {
  cadence?: ScheduleCadence | null;
  expectedIntervalSeconds?: unknown;
  marketSession?: unknown;
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatCadenceAmount = (amount: number, unit: string): string | null => {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const singularUnit = unit.toLowerCase().replace(/s$/, '');
  return amount === 1 ? `Every ${singularUnit}` : `Every ${amount} ${singularUnit}s`;
};

export const formatIntervalCadence = (value: unknown): string | null => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const units = [
    { seconds: 86400, label: 'day' },
    { seconds: 3600, label: 'hour' },
    { seconds: 60, label: 'minute' },
  ];
  const exactUnit = units.find((unit) => seconds % unit.seconds === 0);
  return exactUnit
    ? formatCadenceAmount(seconds / exactUnit.seconds, exactUnit.label)
    : formatCadenceAmount(seconds, 'second');
};

const normalizeDays = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value
    .map((day) => Number(day))
    .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6)))
    .sort((left, right) => left - right);
};

const formatDayCadence = (days: number[], defaultDaily: boolean): string | null => {
  if (days.length === 7 || (days.length === 0 && defaultDaily)) return 'Daily';
  if (days.join(',') === '1,2,3,4,5') return 'Weekdays';
  if (days.join(',') === '0,6') return 'Weekends';
  if (days.length === 1) return `Every ${FULL_DAY_NAMES[days[0]]}`;
  if (days.length > 1) return days.map((day) => DAY_NAMES[day]).join(', ');
  return null;
};

const normalizeTimes = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((time) => String(time || '').trim())
    .filter((time) => /^\d{1,2}:\d{2}$/.test(time))
    .map((time) => {
      const [hour, minute] = time.split(':');
      return `${hour.padStart(2, '0')}:${minute}`;
    });
};

type ViewerCadenceOptions = {
  locale?: string;
  referenceTimestamp?: string | number | Date;
  timeZone?: string;
};

const datePartsInTimeZone = (date: Date, timeZone: string): Record<string, number> => (
  Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  }).formatToParts(date)
    .filter((part) => part.type !== 'literal')
    .map((part) => [part.type, Number(part.value)]))
);

const wallTimeToInstant = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date => {
  const desiredUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
  let candidate = new Date(desiredUtc);

  // Two passes handle offsets that cross a date boundary and daylight-saving changes.
  for (let pass = 0; pass < 2; pass += 1) {
    const actual = datePartsInTimeZone(candidate, timeZone);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second || 0,
    );
    candidate = new Date(candidate.getTime() + desiredUtc - actualAsUtc);
  }

  return candidate;
};

export const formatScheduleCadenceForViewer = (
  value: unknown,
  options: ViewerCadenceOptions = {},
): string | null => {
  if (!value || typeof value !== 'object') return null;
  const schedule = value as ScheduleLike;
  const cadence = schedule.cadence && typeof schedule.cadence === 'object'
    ? schedule.cadence
    : schedule;
  const type = String(cadence.type || '').trim().toLowerCase();
  const times = normalizeTimes(cadence.times);
  const days = normalizeDays(cadence.daysOfWeek);

  // Daily schedules remain daily in every timezone, so the time can be localized
  // without changing the schedule's meaning. Restricted weekday schedules retain
  // their canonical source-timezone label until day shifting is represented too.
  if ((type === 'specific_times' || type === 'weekly') && times.length === 1 && days.length === 7) {
    const sourceTimeZone = String(cadence.timezone || 'UTC').trim() || 'UTC';
    const targetTimeZone = options.timeZone
      || Intl.DateTimeFormat().resolvedOptions().timeZone
      || sourceTimeZone;
    const reference = new Date(options.referenceTimestamp ?? Date.now());
    const sourceDate = datePartsInTimeZone(reference, sourceTimeZone);
    const [hour, minute] = times[0].split(':').map(Number);
    const instant = wallTimeToInstant(
      sourceDate.year,
      sourceDate.month,
      sourceDate.day,
      hour,
      minute,
      sourceTimeZone,
    );
    const localTime = new Intl.DateTimeFormat(options.locale || 'en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: targetTimeZone,
      timeZoneName: 'short',
    }).format(instant);
    return `Daily at ${localTime}`;
  }

  return formatScheduleCadence(value);
};

const joinList = (values: string[]): string => {
  if (values.length <= 1) return values[0] || '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
};

const formatExactTimes = (schedule: ScheduleLike, cadence: ScheduleCadence): string | null => {
  const times = normalizeTimes(cadence.times);
  if (!times.length) return null;
  const timezone = String(cadence.timezone || '').trim();
  const cadenceType = String(cadence.type || '').toLowerCase();
  const marketSession = String(schedule.marketSession || '').toLowerCase();
  const configuredDays = normalizeDays(cadence.daysOfWeek);
  const days = configuredDays.length || cadenceType !== 'specific_times'
    ? configuredDays
    : marketSession === 'regular'
      ? [1, 2, 3, 4, 5]
      : [0, 1, 2, 3, 4, 5, 6];
  const dayText = formatDayCadence(days, false);
  return [dayText, `at ${joinList(times)}`, timezone].filter(Boolean).join(' ');
};

const formatMonthlyCadence = (cadence: ScheduleCadence): string | null => {
  const times = normalizeTimes(cadence.times);
  const timezone = String(cadence.timezone || '').trim();
  const days = Array.isArray(cadence.daysOfMonth)
    ? cadence.daysOfMonth.map(Number).filter((day) => Number.isInteger(day) && day >= 1 && day <= 31)
    : [];
  let dateText = days.length ? `Monthly on day ${joinList(days.map(String))}` : '';
  const ordinal = Number(cadence.ordinalWeekday?.ordinal);
  const weekday = Number(cadence.ordinalWeekday?.weekday);
  if (!dateText && Number.isInteger(ordinal) && ordinal >= 1 && ordinal <= 5 && Number.isInteger(weekday) && weekday >= 0 && weekday <= 6) {
    const suffix = ordinal === 1 ? 'st' : ordinal === 2 ? 'nd' : ordinal === 3 ? 'rd' : 'th';
    dateText = `Monthly on the ${ordinal}${suffix} ${FULL_DAY_NAMES[weekday]}`;
  }
  if (!dateText) return null;
  return [dateText, times.length ? `at ${joinList(times)}` : null, timezone].filter(Boolean).join(' ');
};

const formatMarketEventCadence = (cadence: ScheduleCadence): string | null => {
  const event = String(cadence.event || '').trim().toLowerCase();
  if (event !== 'open' && event !== 'close') return null;
  const market = String(cadence.market || '').trim() === 'ALWAYS_OPEN' ? 'Always-open market' : 'US market';
  const offset = Number(cadence.offsetMinutes);
  if (Number.isFinite(offset) && offset !== 0) {
    return `${Math.abs(offset)} minutes ${offset > 0 ? 'after' : 'before'} ${market} ${event}`;
  }
  return `At ${market} ${event}`;
};

export const formatScheduleCadence = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null;
  const schedule = value as ScheduleLike;
  const cadence = schedule.cadence && typeof schedule.cadence === 'object'
    ? schedule.cadence
    : schedule;
  const type = String(cadence.type || '').trim().toLowerCase();

  if (type === 'specific_times' || type === 'weekly') {
    const exactTimes = formatExactTimes(schedule, cadence);
    if (exactTimes) return exactTimes;
  }
  if (type === 'monthly') {
    const monthly = formatMonthlyCadence(cadence);
    if (monthly) return monthly;
  }
  if (type === 'market_event') {
    const marketEvent = formatMarketEventCadence(cadence);
    if (marketEvent) return marketEvent;
  }

  const intervalSeconds = cadence.intervalSeconds
    ?? cadence.interval_seconds
    ?? cadence.seconds
    ?? schedule.expectedIntervalSeconds;
  const intervalText = formatIntervalCadence(intervalSeconds);
  if (intervalText) return intervalText;

  const intervalValue = cadence.intervalValue ?? cadence.interval_value;
  const intervalUnit = String(cadence.intervalUnit ?? cadence.interval_unit ?? '').trim();
  if (intervalValue && intervalUnit) return formatCadenceAmount(Number(intervalValue), intervalUnit);

  const semanticLabel = String(cadence.label || '').trim();
  if (semanticLabel && !/^(scheduled|weekly scheduled|monthly scheduled|interval scheduled) run$/i.test(semanticLabel)) {
    return semanticLabel;
  }
  return null;
};
